import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { adminApi } from "@/lib/api";
import { Inbox, FileCheck2, Vote, AlertTriangle, EyeOff, BellRing, CheckCircle2, ChevronRight, Lock, MessageSquarePlus } from "lucide-react";

// Gate Reminder Nudge — human-step labels derived from live Launch Gate keys.
// Never manually typed: the list below only maps gate keys to plain language;
// which items appear comes entirely from the Launch Gate API's required_red.
const HUMAN_STEP_LABELS = {
  voter_journey_human: "Unaided voter rehearsal",
  candidate_journey_human: "Unaided candidate rehearsal",
  observations_reviewed: "Admin rehearsal review",
  usability_failures_resolved: "Resolve critical usability failures",
};

export default function Dashboard() {
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState([]);
  const [elections, setElections] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [munis, setMunis] = useState([]);
  const [attention, setAttention] = useState(null);
  const [gate, setGate] = useState(null);
  const [qaPending, setQaPending] = useState(null);
  const [digest, setDigest] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/attention").then((r) => setAttention(r.data)).catch(() => {});
    adminApi.get("/admin/applications").then((r) => setApps(r.data)).catch(() => {});
    adminApi.get("/admin/declarations/stats").then((r) => setStats(r.data)).catch(() => {});
    adminApi.get("/admin/elections").then((r) => setElections(r.data)).catch(() => {});
    adminApi.get("/admin/incidents").then((r) => setIncidents(r.data)).catch(() => {});
    adminApi.get("/admin/municipalities").then((r) => setMunis(r.data)).catch(() => {});
    adminApi.get("/admin/launch-gate").then((r) => setGate(r.data)).catch(() => {});
    adminApi.get("/admin/beta/feedback-digest").then((r) => setDigest(r.data)).catch(() => {});
    adminApi.get("/public/qa/questions").then((r) => {
      const open = (r.data || []).filter((q) => q.status === "open" && (q.answered || 0) < (q.invited || 0));
      setQaPending(open.length > 0
        ? { invited: open.reduce((s, q) => s + q.invited, 0), answered: open.reduce((s, q) => s + q.answered, 0) }
        : null);
    }).catch(() => {});
  }, []);

  const pendingApps = apps.filter((a) => ["new", "pending_verification", "incomplete"].includes(a.status)).length;
  const totalDecls = stats.reduce((s, r) => s + r.total, 0);
  const openIncidents = incidents.filter((i) => i.status === "open").length;
  const muniName = (id) => munis.find((m) => m.id === id)?.name || "";
  const urgent = (attention?.items || []).filter((i) => i.count > 0);
  const clear = (attention?.items || []).filter((i) => i.count === 0);

  // Derived automatically from the live Launch Gate — never manually typed.
  const humanSteps = gate
    ? (gate.required_red || []).filter((k) => HUMAN_STEP_LABELS[k]).map((k) => HUMAN_STEP_LABELS[k])
    : [];
  const otherRed = gate
    ? (gate.required_red || []).filter((k) => !HUMAN_STEP_LABELS[k])
    : [];
  const nudgeSteps = [
    ...(qaPending ? [`Steve real Q&A submission (${qaPending.answered} of ${qaPending.invited} answered)`] : []),
    ...humanSteps,
  ];

  return (
    <div className="max-w-6xl">
      <h1 className="font-heading font-bold text-2xl text-foreground mb-6" data-testid="admin-dashboard-title">Dashboard</h1>

      {/* GATE REMINDER NUDGE — factual, auto-derived from the actual Launch Gate */}
      {gate && !gate.live_email_allowed && (
        <Card className="mb-6 border-slate-300 bg-slate-50" data-testid="launch-reminder-card">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-start gap-3">
              <Lock className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1 basis-60">
                <p className="text-sm font-semibold text-foreground" data-testid="launch-reminder-title">
                  Launch remains locked.
                </p>
                {nudgeSteps.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-0.5 break-words" data-testid="launch-reminder-steps">
                    Human steps remaining: {nudgeSteps.join(", ")}.
                  </p>
                )}
                {otherRed.length > 0 && (
                  <p className="text-[11px] text-muted-foreground mt-0.5 break-words">
                    Plus {otherRed.length} other required gate{otherRed.length === 1 ? "" : "s"} red: {otherRed.join(", ")}.
                    Email mode: {gate.email_mode}.
                  </p>
                )}
              </div>
              <Link to="/admin/launch-gate"
                    className="text-xs font-medium text-primary hover:underline shrink-0 inline-flex items-center gap-1"
                    data-testid="launch-reminder-link">
                Open Launch Gate <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </CardContent>
        </Card>
      )}
      {gate && gate.live_email_allowed && (
        <Card className="mb-6 border-emerald-300 bg-emerald-50" data-testid="launch-reminder-card">
          <CardContent className="p-4">
            <p className="text-sm text-emerald-800 inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> All required launch gates are green.
              LIVE still requires the deliberate GO LIVE confirmation on the Email Mode page.
            </p>
          </CardContent>
        </Card>
      )}

      {/* BETA FEEDBACK — THIS WEEK (Phase 12.13 digest; grouping never auto-closes reports) */}
      {digest && (
        <Card className="mb-6" data-testid="beta-feedback-digest-card">
          <CardContent className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h2 className="font-heading font-bold text-base tracking-wide inline-flex items-center gap-2.5">
                <MessageSquarePlus className="h-5 w-5 text-accent" /> BETA FEEDBACK — THIS WEEK
              </h2>
              <div className="flex items-center gap-2">
                <Badge variant="outline"
                       className={digest.week_total > 0 ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-secondary text-muted-foreground border-slate-300"}
                       data-testid="beta-digest-week-total">
                  {digest.week_total} new submission{digest.week_total === 1 ? "" : "s"}
                </Badge>
                {digest.pending_new_total > 0 && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-900 border-amber-300"
                         data-testid="beta-digest-pending">
                    {digest.pending_new_total} awaiting review
                  </Badge>
                )}
              </div>
            </div>
            {digest.week_total === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="beta-digest-empty">
                No beta feedback submitted in the last 7 days.
              </p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5" data-testid="beta-digest-categories">
                {digest.categories.map((c) => (
                  <div key={c.category} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-1.5">
                    <span className="text-xs text-foreground">{c.label}</span>
                    <span className="font-heading font-bold text-sm tabular-nums">{c.count}</span>
                  </div>
                ))}
              </div>
            )}
            {digest.repeated_themes?.length > 0 && (
              <div className="mt-3" data-testid="beta-digest-themes">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Repeated themes</p>
                {digest.repeated_themes.map((t) => (
                  <p key={t.theme} className="text-xs text-foreground py-0.5">• {t.sentence}</p>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-2 mt-4">
              <p className="text-[10px] text-muted-foreground italic max-w-md">{digest.note}</p>
              <Link to="/admin/beta"
                    className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                    data-testid="beta-digest-review-link">
                REVIEW BETA FEEDBACK <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* NEEDS YOUR ATTENTION */}
      <Card className="mb-6 border-amber-300" data-testid="needs-attention-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <BellRing className="h-5 w-5 text-amber-600" />
            <h2 className="font-heading font-bold text-base tracking-wide">NEEDS YOUR ATTENTION</h2>
            {attention && (
              <Badge variant="outline" className={attention.total > 0 ? "bg-amber-50 text-amber-900 border-amber-300" : "bg-emerald-50 text-emerald-800 border-emerald-200"} data-testid="attention-total-badge">
                {attention.total} item{attention.total !== 1 ? "s" : ""}
              </Badge>
            )}
          </div>
          {!attention && <p className="text-sm text-muted-foreground">Loading…</p>}
          {attention && urgent.length === 0 && (
            <p className="text-sm text-emerald-700 inline-flex items-center gap-2" data-testid="attention-all-clear">
              <CheckCircle2 className="h-4 w-4" /> All clear — nothing needs action right now.
            </p>
          )}
          {urgent.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5 mb-4" data-testid="attention-urgent-grid">
              {urgent.map((item) => (
                <Link key={item.key} to={item.link} data-testid={`attention-item-${item.key}`}
                      className="flex items-center justify-between rounded-lg border border-amber-300 bg-amber-50 px-3.5 py-2.5 hover:bg-amber-100 group">
                  <span className="text-xs font-semibold text-amber-950">{item.label}</span>
                  <span className="inline-flex items-center gap-1 font-heading font-bold text-amber-900 tabular-nums">
                    {item.count} <ChevronRight className="h-3.5 w-3.5 opacity-60 group-hover:opacity-100" />
                  </span>
                </Link>
              ))}
            </div>
          )}
          {attention && clear.length > 0 && (
            <details>
              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                {clear.length} monitored categories with nothing outstanding
              </summary>
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {clear.map((item) => (
                  <Link key={item.key} to={item.link}>
                    <Badge variant="outline" className="text-[10px] text-slate-500 hover:bg-slate-100">{item.label}: 0</Badge>
                  </Link>
                ))}
              </div>
            </details>
          )}
        </CardContent>
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card><CardContent className="p-5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Pending applications</p>
            <Inbox className="h-4 w-4 text-slate-400" />
          </div>
          <p className="font-heading font-bold text-3xl mt-2 tabular-nums" data-testid="dashboard-pending-apps">{pendingApps}</p>
          <Link to="/admin/applications" className="text-xs text-primary font-medium hover:underline">Review queue</Link>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Declarations</p>
            <FileCheck2 className="h-4 w-4 text-slate-400" />
          </div>
          <p className="font-heading font-bold text-3xl mt-2 tabular-nums" data-testid="dashboard-total-declarations">{totalDecls}</p>
          <Link to="/admin/declarations" className="text-xs text-primary font-medium hover:underline">Open review queue</Link>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Elections</p>
            <Vote className="h-4 w-4 text-slate-400" />
          </div>
          <p className="font-heading font-bold text-3xl mt-2 tabular-nums">{elections.length}</p>
          <p className="text-xs text-muted-foreground">{elections.filter((e) => e.status === "open").length} open</p>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Open incidents</p>
            <AlertTriangle className="h-4 w-4 text-slate-400" />
          </div>
          <p className="font-heading font-bold text-3xl mt-2 tabular-nums">{openIncidents}</p>
          <p className="text-xs text-muted-foreground">Quarantined uploads</p>
        </CardContent></Card>
      </div>

      <Card className="mt-6">
        <CardContent className="p-5">
          <div className="flex items-start gap-3 mb-4">
            <EyeOff className="h-4 w-4 text-accent mt-0.5" />
            <div>
              <h2 className="font-heading font-semibold text-base">Declaration totals — admin only</h2>
              <p className="text-xs text-muted-foreground">
                These totals are never published while any election is open. No public or candidate-facing endpoint exposes them.
              </p>
            </div>
          </div>
          <Table data-testid="dashboard-stats-table">
            <TableHeader>
              <TableRow>
                <TableHead>Candidate</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Test</TableHead>
                <TableHead className="text-right">Live</TableHead>
                <TableHead>By status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground text-sm py-6">No declarations yet</TableCell></TableRow>
              )}
              {stats.map((s) => (
                <TableRow key={s.candidate_id}>
                  <TableCell className="font-medium">{s.candidate_name || s.candidate_id}</TableCell>
                  <TableCell className="text-right tabular-nums">{s.total}</TableCell>
                  <TableCell className="text-right tabular-nums">{s.test}</TableCell>
                  <TableCell className="text-right tabular-nums">{s.live}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(s.by_status).map(([k, v]) => (
                        <Badge key={k} variant="outline" className="text-[10px]">{k}: {v}</Badge>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardContent className="p-5">
          <h2 className="font-heading font-semibold text-base mb-4">Elections</h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Election</TableHead>
                <TableHead>Municipality</TableHead>
                <TableHead>Voting window</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {elections.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="font-medium">{e.label} ({e.election_year})</TableCell>
                  <TableCell>{muniName(e.municipality_id)}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {e.voting_opens_at?.slice(0, 10)} → {e.voting_closes_at?.slice(0, 10)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={
                      e.status === "open" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
                      e.status === "frozen" ? "bg-blue-50 text-blue-800 border-blue-200" :
                      "bg-slate-100 text-slate-700 border-slate-300"
                    }>{e.status}</Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
